import jsPDF from 'jspdf';
import { imageUrlToBase64 } from './imageToBase64';
import { QuizProfile, BlockScores, quizQuestions } from '@/components/supplier-quiz/quizData';

const AMEUP_LOGO_URL = 'https://qfozcnxbtjjmidhjdepc.supabase.co/storage/v1/object/public/logo/Logo_AmeUp_Reajustado.png';

interface QuizPDFData {
  name: string;
  profile: QuizProfile;
  totalScore: number;
  blockScores: BlockScores;
  answers: Record<number, number>;
}

const blockNames: Record<keyof BlockScores, string> = {
  sobrecargaMental: 'Sobrecarga Mental',
  comunicacao: 'Comunicação',
  delegacao: 'Delegação',
  financeiro: 'Financeiro',
};

const getScoreLabel = (value: number): string => {
  const labels = ['Discordo totalmente', 'Discordo', 'Neutro', 'Concordo', 'Concordo totalmente'];
  return labels[value - 1] || 'N/A';
};

export const generateQuizPDF = async (data: QuizPDFData): Promise<jsPDF> => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let yPosition = 20;

  // === HEADER MINIMALISTA ===
  // Logo pequeno
  try {
    const logoData = await imageUrlToBase64(AMEUP_LOGO_URL);
    const logoHeight = 18;
    const logoWidth = (logoData.width / logoData.height) * logoHeight;
    const logoX = 15;
    const logoY = 12;
    pdf.addImage(logoData.base64, 'PNG', logoX, logoY, logoWidth, logoHeight);

    // Título discreto ao lado do logo
    pdf.setTextColor(51, 51, 51); // #333
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    const titleX = logoX + logoWidth + 10;
    pdf.text('Diagnóstico do Negócio', titleX, 23);
  } catch (error) {
    pdf.setTextColor(51, 51, 51);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text('AmeUp - Diagnóstico do Negócio', 15, 23);
  }

  // Linha separadora sutil
  pdf.setDrawColor(224, 224, 224); // #e0e0e0
  pdf.setLineWidth(0.3);
  pdf.line(15, 35, pageWidth - 15, 35);

  yPosition = 50;

  // Nome do participante e data
  pdf.setTextColor(102, 102, 102); // #666
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Participante: ${data.name}`, 15, yPosition);
  yPosition += 6;
  pdf.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, 15, yPosition);

  yPosition += 18;

  // === RESULTADO DO PERFIL ===
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(51, 51, 51);
  pdf.text('Seu Perfil', 15, yPosition);

  yPosition += 8;
  pdf.setFontSize(12);
  pdf.setTextColor(251, 88, 58); // #FB583A - cor de destaque
  pdf.text(data.profile.title, 15, yPosition);

  yPosition += 8;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(102, 102, 102);
  
  const descriptionLines = pdf.splitTextToSize(data.profile.description, pageWidth - 30);
  pdf.text(descriptionLines, 15, yPosition);
  yPosition += descriptionLines.length * 4 + 12;

  // Pontuação Total
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(51, 51, 51);
  pdf.text(`Pontuação Total: ${data.totalScore}/50 pontos`, 15, yPosition);

  yPosition += 16;

  // === PONTUAÇÃO POR ÁREA ===
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(51, 51, 51);
  pdf.text('Pontuação por Área', 15, yPosition);

  yPosition += 8;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(80, 80, 80);

  const blockMaxScores: Record<keyof BlockScores, number> = {
    sobrecargaMental: 15,
    comunicacao: 15,
    delegacao: 10,
    financeiro: 10,
  };

  Object.entries(data.blockScores).forEach(([key, score]) => {
    const blockKey = key as keyof BlockScores;
    const maxScore = blockMaxScores[blockKey];
    const percentage = Math.round((score / maxScore) * 100);
    pdf.text(`• ${blockNames[blockKey]}: ${score}/${maxScore} (${percentage}%)`, 20, yPosition);
    yPosition += 6;
  });

  yPosition += 12;

  // === RESPOSTAS DETALHADAS ===
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(51, 51, 51);
  pdf.text('Suas Respostas', 15, yPosition);

  yPosition += 10;
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(80, 80, 80);

  quizQuestions.forEach((question, index) => {
    // Check if we need a new page
    if (yPosition > pageHeight - 50) {
      pdf.addPage();
      yPosition = 20;
    }

    const answer = data.answers[index];
    const questionText = pdf.splitTextToSize(`${index + 1}. ${question.question}`, pageWidth - 30);
    
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(51, 51, 51);
    pdf.text(questionText, 15, yPosition);
    yPosition += questionText.length * 4 + 2;

    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(102, 102, 102);
    pdf.text(`Resposta: ${getScoreLabel(answer)}`, 20, yPosition);
    yPosition += 8;
  });

  // === FOOTER MINIMALISTA ===
  // Adiciona footer em todas as páginas
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    
    const footerY = pageHeight - 25;
    
    // Linha separadora sutil
    pdf.setDrawColor(224, 224, 224);
    pdf.setLineWidth(0.3);
    pdf.line(15, footerY, pageWidth - 15, footerY);

    // Tagline
    pdf.setTextColor(51, 51, 51);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Digitalize, Cresça e Multiplique', pageWidth / 2, footerY + 10, { align: 'center' });

    // Contato
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(102, 102, 102);
    pdf.text('(11) 91493-5333  |  www.ameup.com.br', pageWidth / 2, footerY + 17, { align: 'center' });
  }

  return pdf;
};
